// What is true at an instant. Pure, and the only part of this package a
// screen touches on every render.
//
// ICD-S05 §7.5: A LOCK IS COMPUTED, NEVER STORED AS TRUTH. Nothing in this
// file reads a `locked_at` column or asks a database anything; a lock is
// `lockAt <= now` and a period's status is a comparison against its bounds. So
// a sweep that is ten minutes late cannot leave a lineup editable past its
// kickoff, because nothing was ever waiting on the sweep to say so.
//
// THE TWO LOCKS, AND WHY BOTH ARE RIGHT. A WINDOW locks per the league's
// `lockRule`. A ROW locks at its own player's kickoff, unconditionally, and
// that half is already built in `@fsp/domain`'s `lineupRowFromDetail`. A
// fixed-time league that locks at 1:00 pm therefore still cannot start a
// player whose London game kicked off at 9:30 — the window is open and the row
// is not. Clamping the window lock back to the first kickoff was the obvious
// alternative and is wrong: it would make the plain-English rule ("lineups
// lock at 1:00 pm") a lie on any week with an early game.

import type {
  ClockViewShape,
  LeagueSettings,
  PeriodStateShape,
  WindowStateShape,
} from '@fsp/domain';
import type { Game, LineupWindow, Period } from '@fsp/sports-core';
import { runHappensAt } from '@fsp/waivers';
import type { ClockContext } from './types';
import { wallClockOnDayOf, wallClocksBetween } from './zone';

const iso = (at: Date): string => at.toISOString();
const ms = (at: string): number => Date.parse(at);

/** Games that start inside a window, soonest first. A canceled game is not one. */
export function gamesInWindow(window: LineupWindow, games: readonly Game[]): Game[] {
  const from = ms(window.start);
  const to = ms(window.end);
  return games
    .filter((g) => g.status !== 'canceled')
    .filter((g) => {
      const at = ms(g.start);
      return at >= from && at < to;
    })
    .sort((a, b) => ms(a.start) - ms(b.start));
}

/**
 * The first instant anything in a window locks, per the league's rule: the
 * window's one lock, or under `per-player-game` its first kickoff. What a
 * lineup warning is timed against (`notify.lineup.*`, NOTIFICATIONS.md §3.4,
 * D3) — the planner and the handler both ask this, so they cannot disagree
 * about which job is which lead. Null when no game falls in the window.
 *
 * A STORED WINDOW CARRIES ITS FIRST KICKOFF, and the window lock is computed
 * from it as `planJobs` computes `lineup.lock`'s; a window without one is read
 * from the games.
 */
export function firstLockAt(
  settings: LeagueSettings,
  window: LineupWindow & { firstKickoff?: string | null },
  games: readonly Game[],
): number | null {
  const first = gamesInWindow(window, games)[0];
  if (settings.lineup.lockRule === 'per-player-game') return first ? ms(first.start) : null;
  const lock = lockFromKickoff(
    settings,
    window.firstKickoff !== undefined ? window.firstKickoff : (first?.start ?? null),
  );
  return lock === null ? null : ms(lock);
}

/**
 * When this window's lineup stops being editable as a whole, per the league's
 * rule. Null under `per-player-game`, where there is no single instant, and
 * null when no game falls in the window at all (§7.10 — a bye is not an error).
 */
export function windowLockAt(
  settings: LeagueSettings,
  window: LineupWindow,
  games: readonly Game[],
): { firstKickoff: string | null; lockAt: string | null } {
  const inside = gamesInWindow(window, games);
  const firstKickoff = inside.length > 0 ? inside[0]!.start : null;
  return { firstKickoff, lockAt: lockFromKickoff(settings, firstKickoff) };
}

/**
 * The same answer from a kickoff alone, with no game list.
 *
 * Split out because the stored calendar carries each window's `firstKickoff`
 * as a world fact and computes the lock from the league's CURRENT rule — so
 * changing `lockRule` moves every future lock without re-reading a schedule,
 * and a kickoff that moved can be compared against what was stored.
 */
export function lockFromKickoff(
  settings: LeagueSettings,
  firstKickoff: string | null,
): string | null {
  if (firstKickoff === null) return null;
  switch (settings.lineup.lockRule) {
    case 'per-player-game':
      return null;
    case 'first-game':
      return firstKickoff;
    case 'fixed-time': {
      // Validation already refuses a fixed-time league with no time
      // (`validate.ts`), so this fallback is for a stored version that predates
      // the rule rather than for a configuration anybody can make today.
      if (!settings.lineup.fixedLockTime) return firstKickoff;
      // The stated time on the local day of the window's first kickoff. Not on
      // the window's start day, which is three days earlier and would lock a
      // lineup on Wednesday for a Sunday game.
      return iso(
        wallClockOnDayOf(
          settings.identity.timeZone,
          new Date(firstKickoff),
          settings.lineup.fixedLockTime,
        ),
      );
    }
    default:
      return firstKickoff;
  }
}

/** One window, as of an instant. */
export function windowStateOf(
  settings: LeagueSettings,
  window: LineupWindow,
  games: readonly Game[],
  now: Date,
): WindowStateShape {
  const { firstKickoff, lockAt } = windowLockAt(settings, window, games);
  return {
    id: window.id,
    start: window.start,
    end: window.end,
    firstKickoff,
    lockAt,
    locked: lockAt !== null && ms(lockAt) <= now.getTime(),
  };
}

/** One period, as of an instant. */
export function periodStateOf(
  settings: LeagueSettings,
  period: Period,
  games: readonly Game[],
  now: Date,
  kind: PeriodStateShape['kind'] = 'regular',
): PeriodStateShape {
  const at = now.getTime();
  const status: PeriodStateShape['status'] =
    at < ms(period.start) ? 'pre' : at >= ms(period.end) ? 'final' : 'live';
  return {
    season: period.season,
    index: period.index,
    label: period.label,
    kind,
    start: period.start,
    end: period.end,
    status,
    windows: period.windows.map((w) => windowStateOf(settings, w, games, now)),
    proBye: [...period.flags.proBye],
    playoffEligible: period.flags.playoffEligible,
    // A period whose end has passed has been played and scored under. §7.7.
    frozen: at >= ms(period.end),
  };
}

/**
 * Which kind a period is, from the league's own schedule settings.
 *
 * THE SEASON ENDS WHEN THE BRACKET DOES. Until 2026-09-19 every period after
 * the regular season answered `playoff`, which meant a league whose bracket
 * ran weeks 15 and 16 still had two more "playoff" periods after it in an
 * eighteen-week sport — so `period.open` pushed a finished league back into
 * `playoffs`, and `season.rollover` was planned a fortnight after the
 * championship. `playoffs.periods` is what the rules actually reserve, so it
 * is what this reads; everything past it is off-season. BACKLOG E9a found it.
 *
 * A league with no playoffs (`teamCount: 0`, no periods) ends with its regular
 * season, which is the same rule and not a special case.
 */
export function periodKind(settings: LeagueSettings, index: number): PeriodStateShape['kind'] {
  const reg = settings.schedule.regularSeason;
  if (index < reg.firstPeriod) return 'off';
  if (index <= reg.lastPeriod) return 'regular';
  return settings.playoffs.periods.includes(index) ? 'playoff' : 'off';
}

/**
 * Every instant at or after `now` at which something on this league's lineups
 * becomes uneditable — window locks AND individual kickoffs, because a row
 * locks at its own game whatever the league rule says.
 */
function locksAfter(
  settings: LeagueSettings,
  periods: readonly Period[],
  games: readonly Game[],
  now: Date,
): number[] {
  const at = now.getTime();
  const found: number[] = [];
  for (const period of periods) {
    for (const window of period.windows) {
      const { lockAt } = windowLockAt(settings, window, games);
      if (lockAt !== null && ms(lockAt) > at) found.push(ms(lockAt));
      for (const game of gamesInWindow(window, games)) {
        if (ms(game.start) > at) found.push(ms(game.start));
      }
    }
  }
  return found.sort((a, b) => a - b);
}

/** How far before a range a run can be scheduled and still move into it: a week and a day. */
const RUN_MOVE_REACH_MS = 8 * 24 * 60 * 60 * 1000;

/**
 * Every waiver run in `(after, until]`, where it happens: at the league's
 * processing time, or later where a late week moved it (`runHappensAt`,
 * B-507). Empty for a league with no waivers or a continuous schedule. The
 * planner's runs and the clock's next one (`nextWaiverRun`) both read this.
 */
export function waiverRunsBetween(
  settings: LeagueSettings,
  periods: readonly Period[],
  after: Date,
  until: Date,
): number[] {
  const p = settings.waivers.processing;
  if (settings.waivers.system === 'none' || p.schedule === 'continuous') return [];
  const zone = settings.identity.timeZone;
  const weeks = periods.map((x) => ({ start: ms(x.start), end: ms(x.end) }));
  const weekday = p.schedule === 'weekly' ? (p.weekday ?? 3) : null;
  const from = new Date(after.getTime() - RUN_MOVE_REACH_MS);
  return wallClocksBetween(zone, from, until, p.time, weekday)
    .map((at) => runHappensAt(p, zone, weeks, at.getTime()))
    .filter((at) => at > after.getTime() && at <= until.getTime());
}

/**
 * The next instant claims are processed, resolved through the league's zone,
 * where it happens: a late week moves it (B-507), so it is read off the
 * league's calendar as the planner's runs are.
 *
 * NULL FOR A LEAGUE WITH NO WAIVERS (PLAN-2026-09-24 rule 9, check 32). A
 * league whose system is `none` is first come, first served: a drop is a free
 * agent at once and nothing is ever claimed, so there is no run to name. It
 * named one anyway until W6 — Wednesday at 3:00 AM for a league that had
 * chosen to have no Wednesdays — because this read the processing time and
 * never asked whether anything is processed.
 */
export function nextWaiverRun(
  settings: LeagueSettings,
  periods: readonly Period[],
  now: Date,
): Date | null {
  // Two reaches: the next scheduled run can be a week off, and moved past that.
  const ahead = new Date(now.getTime() + 2 * RUN_MOVE_REACH_MS);
  const next = waiverRunsBetween(settings, periods, now, ahead)[0];
  return next === undefined ? null : new Date(next);
}

/** What time it is for one league. ICD-S05 §4. */
export function readClock(ctx: ClockContext, now: Date): ClockViewShape {
  const { league, periods, games, lastSweepAt } = ctx;
  const settings = league.settings;
  const at = now.getTime();

  const states = periods.map((p) =>
    periodStateOf(settings, p, games, now, periodKind(settings, p.index)),
  );
  const current = states.find((p) => at >= ms(p.start) && at < ms(p.end)) ?? null;
  const next = states.find((p) => ms(p.start) > at) ?? null;

  const upcoming = locksAfter(settings, periods, games, now);
  const deadlinePeriod = settings.trades.deadlinePeriod;
  const deadline =
    deadlinePeriod === null ? null : (states.find((p) => p.index === deadlinePeriod)?.end ?? null);
  const waiverAt = nextWaiverRun(settings, periods, now);

  return {
    now: iso(now),
    timeZone: settings.identity.timeZone,
    current,
    next,
    nextLockAt: upcoming.length > 0 ? iso(new Date(upcoming[0]!)) : null,
    nextWaiverRunAt: waiverAt ? iso(waiverAt) : null,
    tradeDeadlineAt: deadline,
    draftAt: settings.draft.scheduledAt,
    // Null is "never swept", which is not zero. B-91.
    behindBy:
      lastSweepAt === null ? null : Math.max(0, Math.floor((at - lastSweepAt.getTime()) / 1000)),
  };
}
